/**
 * Server-driven lobby config (B4, ADR-062).
 *
 * Own module per the B3 convention so shared `client.ts` / `types.ts` stay
 * untouched. The wire shape mirrors `server/src/settings/settings.service.ts`
 * (`lobbySchema`) field for field — a rename on either side breaks the other.
 *
 * The panel edits the STORED config; the server applies the drop rules
 * (unknown slugs, empty rails) when it serves `GET /games`, not here. The
 * editor still warns on a slug the catalogue does not currently serve, because
 * an operator typing a typo should hear about it now, not silently ship a rail
 * the client never shows.
 */
import { http } from './http.ts';
import { MOCK_MODE } from './client.ts';

export type LobbyBadge = 'new' | 'hot' | 'live' | 'top';

export const LOBBY_BADGES: LobbyBadge[] = ['new', 'hot', 'live', 'top'];

export interface LobbyRail {
  /** Stable machine id — lowercase letters, digits, `_`, `-`. */
  id: string;
  /** The heading the player reads. */
  title: string;
  /** Ordered game slugs. */
  slugs: string[];
}

export interface LobbyConfig {
  rails?: LobbyRail[];
  badges?: Record<string, LobbyBadge>;
}

/** LIVE — `GET /internal/admin/settings/lobby`. Null when never configured. */
export async function fetchLobbyConfig(): Promise<LobbyConfig | null> {
  if (MOCK_MODE) return null;
  const body = await http.get<{ key: string; value: LobbyConfig | null }>(
    '/internal/admin/settings/lobby',
  );
  return body.value;
}

/**
 * LIVE — `PUT /internal/admin/settings/lobby`. Admin only, zod-validated
 * server-side, audited (`settings.update`). Returns the stored value.
 */
export async function saveLobbyConfig(value: LobbyConfig): Promise<LobbyConfig> {
  const body = await http.put<{ key: string; value: LobbyConfig }>(
    '/internal/admin/settings/lobby',
    { value },
  );
  return body.value;
}
